import {
	prepareKnownSizeUploadSource,
	createKnownSizeUploadTarget,
	bindKnownSizeUploadSource,
	readUploadKnownSizeControl,
	type UploadsCreateRequest
} from "@uploads";
import { scopedAdminUploadApi } from "./adminScopedUploadApi";
import api from "@/api";
import constants from "@/api/constants";
import type { MultipartUploadTarget } from "@/types/uploadTarget";
import {
	beginDeferredFileUploadTarget,
	commitFileUploadObjectChoice,
	getChunkBuffer,
	readFileUploadSession,
	updateFileUploadSession,
	type FileUploadSession
} from "@/db/fileUploadDb";
import {
	rebuildPersistedChunkManifest,
	withSettlingCeiling,
	createLegacyMultipartUploadTarget,
	createUploadsMultipartUploadTarget,
	describeRollbackFailureSummary,
	isUploadManifestInvalidError,
	isUploadsUploadTarget,
	parsePersistedPriorityChunkIndexes,
	planChunkedObjectVideo,
	readChunkedObjectOfferKillSwitch,
	readChunkedObjectRuntimeEnv,
	readUploadFacadeErrorCode,
	UploadAlreadyCompleteAtCreateError,
	type ChunkedObjectTargetBlock,
	type RollbackFailureSummary,
	type UploadTargetEnvelope,
	type PersistedTargetInitiator,
	type UploadTargetInitiator
} from "@uploads";
import { adminUploadTelemetry } from "@/services/uploads/adminUploadTelemetry";
import { uploadFailureLogDetails } from "./uploadLogRedaction";
import {
	extensionForMimeType,
	isPlausibleFileExtension,
	mimeTypeForExtension,
	normalizeMimeType,
	rawExtensionFromFileName
} from "@/helpers/fileUploads";

function normalizeUploadEnvelope(value: unknown): UploadTargetEnvelope {
	if (!value || typeof value !== "object") throw new Error("Uploads facade returned an invalid target envelope");
	return value as UploadTargetEnvelope;
}

function hasUploadsAttemptIdentity(response: unknown): response is {
	id: string;
	attemptId: string;
	generation: number;
	attempt?: { takeGeneration?: string } | null;
	targets?: unknown;
} {
	if (!response || typeof response !== "object") return false;
	const projection = response as {
		id?: unknown;
		attemptId?: unknown;
		generation?: unknown;
		attempt?: { takeGeneration?: unknown } | null;
	};
	return (
		typeof projection.id === "string" &&
		projection.id.trim().length > 0 &&
		typeof projection.attemptId === "string" &&
		projection.attemptId.trim().length > 0 &&
		Number.isSafeInteger(projection.generation) &&
		(projection.generation as number) > 0
	);
}

async function uploadsTargetFromResponse(response: unknown, requestApi = api): Promise<MultipartUploadTarget> {
	// LSP-22828: a replay of an upload the server already finished has no targets to wrap. Raised before
	// the compensating abort below, which must never be sent for a ready upload.
	if (hasUploadsAttemptIdentity(response) && (response as { alreadyComplete?: unknown }).alreadyComplete === true) {
		throw new UploadAlreadyCompleteAtCreateError(response.id);
	}
	try {
		if (!hasUploadsAttemptIdentity(response)) throw new Error("Uploads facade returned an invalid attempt identity");
		const targets = Array.isArray(response.targets) ? response.targets.map(normalizeUploadEnvelope) : [];
		// `response.transport` is the transport the server actually minted and this wrapper assumes
		// multipart unconditionally; the mint-time warn lives on createUploadsMultipartUploadTarget.
		const declaredTransport = (response as { transport?: unknown }).transport;
		return createUploadsMultipartUploadTarget({
			transport: typeof declaredTransport === "string" ? declaredTransport : undefined,
			registryId: response.id,
			attemptId: response.attemptId,
			generation: response.generation,
			takeGeneration: response.attempt?.takeGeneration,
			envelopes: targets
		});
	} catch (error) {
		if (hasUploadsAttemptIdentity(response)) {
			const takeGeneration = response.attempt?.takeGeneration;
			await (
				takeGeneration
					? requestApi.abortUploadsUpload(response.id, response.attemptId, response.generation, "multipart", takeGeneration)
					: requestApi.abortUploadsUpload(response.id, response.attemptId, response.generation, "multipart")
			).catch((cleanupError) => {
				console.warn(
					"[Upload] Freshly minted upload target validation failed and its compensating abort also failed; server upload may be orphaned",
					{
						registryId: response.id,
						attemptId: response.attemptId,
						generation: response.generation,
						takeGeneration
					},
					cleanupError
				);
			});
		}
		throw error;
	}
}

/**
 * A video target minted as `chunked-object` also carries what the durable row must persist to
 * resume on the SAME lane after a reload — the reloaded tab has no File left to walk. Structural
 * twin of contact-async's `AsyncVideoTarget`.
 */
export type AdminVideoTarget = MultipartUploadTarget & { chunkedObject?: ChunkedObjectTargetBlock };

/**
 * Create a `/uploads` target, falling back ONCE to a plain multipart create when the server refuses
 * the byte-offset manifest (400 UPLOAD_MANIFEST_INVALID). The file is still uploadable exactly the
 * way it always was, so the fallback body is today's verbatim. Twin of contact-async's
 * `createAsyncUploadsTarget`; silent-failure rule — name the condition and the reason string.
 */
async function createAdminUploadsTarget(body: Record<string, unknown>, requestApi = api): Promise<MultipartUploadTarget> {
	try {
		return await uploadsTargetFromResponse(await requestApi.createUploadsUpload(body), requestApi);
	} catch (error) {
		if (!isUploadManifestInvalidError(error) || !body.chunkManifest) throw error;
		console.warn("[Upload] Chunked-object manifest refused; falling back to multipart", {
			conversationId: body.conversationId,
			sessionClientId: body.sessionClientId,
			code: readUploadFacadeErrorCode(error),
			message: error instanceof Error ? error.message : String(error)
		});
		adminUploadTelemetry.logWarn(
			"upload.chunkedObject.manifestRefused",
			{ conversationId: body.conversationId, sessionClientId: body.sessionClientId },
			error
		);
		const { chunkManifest: _refusedManifest, ...withoutManifest } = body;
		try {
			return await uploadsTargetFromResponse(
				await requestApi.createUploadsUpload({
					...withoutManifest,
					supportedTransports: (withoutManifest.supportedTransports as string[]).filter(
						(transport) => transport !== "chunked-object"
					)
				}),
				requestApi
			);
		} catch (fallbackError) {
			// Both codes have to stay recoverable: the caller classifies on the multipart refusal, but the
			// manifest refusal that sent us down this path is the only record that an offer was even made.
			if (fallbackError instanceof Error && fallbackError.cause === undefined) fallbackError.cause = error;
			throw fallbackError;
		}
	}
}

function legacyTargetFromResponse(response: unknown): MultipartUploadTarget {
	if (!response || typeof response !== "object") throw new Error("Legacy upload initiation returned an invalid target");
	return createLegacyMultipartUploadTarget({ type: "multipart", ...response } as Parameters<typeof createLegacyMultipartUploadTarget>[0]);
}

function abortUploadsMultipartTarget(target: {
	registryId: string;
	attemptId: string;
	generation: number;
	takeGeneration?: string;
	contractVersion?: 2;
}): Promise<unknown> {
	if (target.contractVersion === 2)
		return api.abortUploadsUpload(target.registryId, target.attemptId, target.generation, "multipart", target.takeGeneration, undefined, 2);
	return target.takeGeneration
		? api.abortUploadsUpload(target.registryId, target.attemptId, target.generation, "multipart", target.takeGeneration)
		: api.abortUploadsUpload(target.registryId, target.attemptId, target.generation, "multipart");
}

/**
 * LSP-19132 break-it round 5: the S3 key extension must agree with the fileType sent alongside it.
 *
 * The fresh mint (`initiateFile`) derives the extension from `file.type`. Both PERSISTED mints below
 * derived it from `session.fileName` instead, which was harmless only while the two always matched.
 * They no longer do: useFileUploadFlow now corrects a mislabeled declared type, so a JPEG named
 * `IMG_0001.heic` has `fileType: image/jpeg` but still carries its original `.heic` name. Deriving from
 * the name then sent the contradictory pair `fileType: image/jpeg` + `fileExtension: heic`, the server
 * minted a key ending `.heic`, and the snapshot Lambda ran heif-convert on JPEG bytes — recreating the
 * exact bug this ticket fixes, for any upload that resumes from its durable record (a reload, a closed
 * tab, a dropped connection — the driver pre-persists every file, so this is a common path).
 *
 * break-it round 6: the filename stays PREFERRED whenever it still agrees with fileType, so this is a
 * strict no-op for every pre-existing session. Deriving from the subtype unconditionally was a real
 * regression: `holiday.jpg` (image/jpeg) became extension `jpeg` rather than the historical `jpg`,
 * changing the S3 key of every RESUMED upload rather than only the corrected ones. The subtype is
 * consulted only when the two genuinely disagree — which is exactly the corrected-file case — or when
 * the name carries no usable extension.
 *
 * `application/octet-stream` is a placeholder, not a description of the bytes; it never wins over a
 * real filename extension. (contact-async's asyncSessionStore ladder makes the same exclusion.)
 * Only images and documents reach here — video sessions take the recording branch above.
 */
function persistedFileExtension(session: { fileType?: string; fileName: string }): string {
	const nameExtension = rawExtensionFromFileName(session.fileName);
	const declaredType = normalizeMimeType(session.fileType);
	// The filename's extension is the DEFAULT. It is only overridden when we have positive evidence
	// that it names different bytes than fileType, AND we can name a better extension for that type.
	//
	// archgap round 5: the earlier version treated "not in EXTENSION_TO_MIME" as disagreement, which
	// broke the strict no-op guarantee for legitimate extensions the table simply does not list —
	// `portrait.jfif` + image/jpeg was rewritten to `jpeg`, and `portrait.JPG` + image/pjpeg to the
	// strictly worse `pjpeg`. Absence from the table is absence of evidence, not evidence of conflict.
	// An extension that is not a plausible extension at all (whitespace, a slash, a 4096-character run,
	// fullwidth or combining-mark forms) is never kept — rounds 2-4 caught each of those reaching the key.
	if (!isPlausibleFileExtension(nameExtension)) {
		return extensionForMimeType(declaredType) || declaredType.split("/")[1] || "";
	}
	const impliedType = mimeTypeForExtension(nameExtension);
	const namesDifferentBytes = impliedType.length > 0 && declaredType.length > 0 && impliedType !== declaredType;
	if (namesDifferentBytes) {
		// This is the corrected-file case the ticket exists for: `IMG_0001.heic` carrying image/jpeg.
		const betterExtension = extensionForMimeType(declaredType);
		if (betterExtension) return betterExtension;
	}
	return nameExtension || extensionForMimeType(declaredType) || declaredType.split("/")[1] || "";
}

/**
 * Gate-4 deletion horizon: this is the sole Admin file legacy drain adapter. It completes
 * sessions persisted before the web replacement deployed; it must not mint a new file session.
 */
async function initiateLegacyPinnedPersistedTarget(
	session: FileUploadSession,
	stableId: string,
	totalChunks: number,
	requestApi = api
): Promise<MultipartUploadTarget> {
	if (session.fileCategory === "video") {
		const result = await requestApi.initiateRecordingMultipartUpload(
			session.conversationId,
			stableId,
			stableId,
			session.fileType,
			totalChunks,
			session.projectChecklistItemId
		);
		return legacyTargetFromResponse(result);
	}
	const extension = persistedFileExtension(session);
	const result = await requestApi.initiateMultipartUpload(
		session.conversationId,
		[
			{
				originalFileName: session.fileName,
				fileExtension: extension,
				fileType: session.fileType,
				...(session.fileCategory === "image" && session.photoTime ? { createdAt: session.photoTime } : {}),
				idempotencyId: stableId
			}
		],
		session.fileCategory === "image" ? constants.DOCUMENT_TYPE.IMAGE : constants.DOCUMENT_TYPE.DOCUMENT,
		totalChunks,
		session.projectChecklistItemId
	);
	return legacyTargetFromResponse(result);
}

function reportAdminInitiationFailure(category: "image" | "document" | "video", error: unknown): void {
	const details = uploadFailureLogDetails(error);
	if (details.httpStatus === 404) return;
	console.error(`Failed to get upload targets for ${category === "video" ? "videos" : `${category}s`}`, details);
}

/**
 * LSP-19605: one warn line per rollback (was one `console.error` per minted target per failed batch —
 * 100 lines in 12 ms under the 2026-09-04 saturation). A bare `console.warn`, not the `logWarn`
 * helper: `plugins/datadog.ts` forwards console warn/info already, and the helper ALSO calls the SDK
 * logger, so it would ingest this summary twice. Warn, not error: an unconfirmed target is at worst
 * an orphaned multipart upload that the server's incomplete-parts sweep reclaims.
 */
function reportAdminRollbackFailures(summary: RollbackFailureSummary<MultipartUploadTarget>): void {
	console.warn(
		`[Upload] Multipart rollback left ${summary.failed.length + summary.unattempted.length + summary.inFlight.length} target(s) unconfirmed — the server's incomplete-parts sweep reclaims any orphan`,
		describeRollbackFailureSummary(summary)
	);
}

/**
 * LSP-19912 — deliberately NOT bounded by the shared settling ceiling.
 *
 * `initiateFile` / `initiateVideo` are the FOREGROUND mint: the user just picked a file and is
 * watching the dialog. Each call is one-shot — a rejection is surfaced to that user immediately
 * (`reportInitiationFailure`, then the dialog's own error state) and nothing re-drives it on a
 * timer, so the wedged-owner loop the ceiling exists to bound cannot form here. The ceiling is
 * only wired into `createAdminPersistedTargetInitiator` below, where a DURABLE row is re-driven
 * by the paced drain across drains, reloads and tabs with no user watching. Should a background
 * re-mint ever be added to this initiator, it needs the ceiling too — see
 * `boundPersistedSessionSettlingConflict`.
 */
export function createAdminUploadTargetInitiator(
	conversationId: string,
	projectChecklistItemId?: string
): UploadTargetInitiator<File, MultipartUploadTarget> {
	return {
		async initiateFile({ file, category, stableId, totalChunks }) {
			const response = await api.createUploadsUpload({
				kind: category === "image" ? "image" : "file",
				conversationId,
				fileDetails: [
					{
						originalFileName: file.name,
						fileExtension: file.type.split("/")[1],
						fileType: file.type,
						idempotencyId: stableId
					}
				],
				type: category === "image" ? constants.DOCUMENT_TYPE.IMAGE : constants.DOCUMENT_TYPE.DOCUMENT,
				supportedTransports: ["multipart"],
				expectedBytes: file.size,
				totalChunks,
				projectChecklistItemId: projectChecklistItemId
			});
			return await uploadsTargetFromResponse(response);
		},
		async initiateVideo({ file, stableId, totalChunks }) {
			// A selected video FILE is a multipart upload, not live capture. It keeps kind "recording"
			// so the object stays a RecordingSession (transcode / product semantics unchanged) but takes
			// the transport that can actually finish: the server's chunk-stream lane completes via
			// /recording/finish, which a file upload never calls, so its parts manifest was refused.
			// expectedBytes + totalChunks are BOTH mandatory on this lane - chooseTransport needs the
			// size and the server presigns exactly totalChunks parts. Live capture stays on chunk-stream.
			// Plan 3a: offer the sealed-upload `chunked-object` transport when this really is an MP4 we
			// can describe by byte offsets. Every decline returns null and mints exactly today's body.
			const intent = await planChunkedObjectVideo(file, { telemetry: adminUploadTelemetry });
			// The caller sliced the file into `totalChunks` already. The manifest is a pure function of
			// (size, chunkSize), so a disagreement means the two used different chunk sizes — offering it
			// would presign a part count the chunks can never fill. Programming error, so decline loudly.
			if (intent && intent.manifest.parts.length !== totalChunks) {
				console.warn("[Upload] Chunked-object manifest disagrees with the caller's chunk count; offering multipart only", {
					conversationId,
					totalChunks,
					manifestParts: intent.manifest.parts.length,
					chunkSizeBytes: intent.chunkSizeBytes
				});
			}
			const offer = intent && intent.manifest.parts.length === totalChunks ? intent : null;
			const target = await createAdminUploadsTarget({
				kind: "recording",
				conversationId,
				sessionClientId: stableId,
				recordingClientId: stableId,
				fileType: file.type,
				totalChunks,
				supportedTransports: offer ? ["chunked-object", "multipart"] : ["multipart"],
				...(offer ? { chunkManifest: offer.manifest } : {}),
				expectedBytes: file.size,
				projectChecklistItemId
			});
			// A DECLINED OFFER IS SILENT ON THE SERVER SIDE OF THIS BOUNDARY TOO. `uploads.transport.chosen`
			// says why the server did not pick it, but an app team watching adoption from the browser has no
			// access to that event — and an offer that never reaches the server at all (a kill switch, a
			// network failure before the create) looks identical from here to one the server refused. One
			// line per declined offer, with the client-side kill switch state, is what separates them.
			if (offer && isUploadsUploadTarget(target) && target.transport !== "chunked-object") {
				adminUploadTelemetry.logInfo("upload.chunkedObject.notSelected", {
					registryId: target.registryId,
					attemptId: target.attemptId,
					// A joined string, not an array: `sanitizeUploadLogContext` is default-deny by field shape
					// and drops arrays, so an array here would silently vanish from the record.
					offeredTransports: "chunked-object,multipart",
					// `createUploadsMultipartUploadTarget` carries `transport` ONLY for chunked-object — an
					// absent one is deliberately not stamped "multipart" on the target itself. Named here
					// rather than left undefined, which the telemetry sanitizer would drop entirely.
					selectedTransport: target.transport ?? "multipart",
					parts: offer.manifest.parts.length,
					layout: offer.layout.kind,
					// The reading that GATED the offer, taken inside `derivePlan`. Re-reading the switch here
					// answers a different question and was therefore structurally false on this event: an
					// offer only exists when planning saw the switch off.
					killSwitchDisabled: offer.killSwitchDisabled,
					// The second reading, taken now. The only one that can be true, and a disagreement with the
					// line above is an operator flipping the switch between planning and target creation —
					// previously invisible, because only one of the two readings was ever recorded.
					killSwitchDisabledAtTargetCreation: readChunkedObjectOfferKillSwitch(readChunkedObjectRuntimeEnv())
				});
			}
			// The SERVER decides. A minted multipart target is today's lane verbatim, extra fields and all
			// absent, so a chunked-object offer that was not taken up costs nothing downstream.
			if (offer && isUploadsUploadTarget(target) && target.transport === "chunked-object") {
				adminUploadTelemetry.logInfo("upload.chunkedObject.selected", {
					registryId: target.registryId,
					attemptId: target.attemptId,
					parts: offer.manifest.parts.length,
					layout: offer.layout.kind,
					priorityChunkCount: offer.priorityChunkIndexes.length
				});
				return {
					...target,
					chunkedObject: { chunkSizeBytes: offer.chunkSizeBytes, priorityChunkIndexes: offer.priorityChunkIndexes }
				} satisfies AdminVideoTarget;
			}
			return target;
		},
		async abortFile(target) {
			if (isUploadsUploadTarget(target)) {
				await abortUploadsMultipartTarget(target);
				return;
			}
			await api.abortMultipartUpload(target.uploadId, target.s3Key, target.bucket);
		},
		async abortVideo(target) {
			if (isUploadsUploadTarget(target)) {
				await abortUploadsMultipartTarget(target);
				return;
			}
			await api.abortMultipartUpload(target.uploadId, target.s3Key, target.bucket);
		},
		reportInitiationFailure: reportAdminInitiationFailure,
		reportRollbackFailures: reportAdminRollbackFailures
	};
}

/**
 * LSP-19912: the durable half of the shared settling ceiling for Admin's large-file/video
 * multipart driver.
 *
 * The window lives on the `FileUploadSession` row this driver already persists in Dexie, so it
 * survives the reload that makes the retry loop unbounded; the rule, the terminal mint and the
 * close-on-success half live in `@uploads/settlingCeiling`. Same shape as contact-async's
 * `mintFileSessionBoundedBySettlingCeiling` and `chunkStreamUploader.createBoundedBySettlingCeiling`
 * — three lanes, three stores, one rule.
 */
async function mintPersistedTargetBoundedBySettlingCeiling(
	session: FileUploadSession,
	mint: () => Promise<MultipartUploadTarget>
): Promise<MultipartUploadTarget> {
	const sessionId = session.id;
	return withSettlingCeiling(mint, {
		// The drain re-reads the row immediately before every attempt, so this IS the durable value.
		knownOpenedAt: () => session.settlingConflictSince,
		window: {
			read: async () => (await readFileUploadSession(sessionId))?.settlingConflictSince,
			arm: async (openedAt) => {
				await updateFileUploadSession(sessionId, { settlingConflictSince: openedAt });
			},
			disarm: async () => {
				await updateFileUploadSession(sessionId, { settlingConflictSince: undefined });
			}
		},
		onExpired: ({ elapsedMs, ceilingMs }) => {
			console.warn("[Upload] File upload session stayed in the server settling window past its ceiling", {
				sessionId,
				elapsedMs,
				ceilingMs
			});
		},
		onSettled: ({ elapsedMs }) => {
			console.info("[Upload] File upload session left the server settling window; its ceiling restarts from the next conflict", {
				sessionId,
				elapsedMs
			});
		},
		onWindowError: (windowError) => {
			// Never upgrade a retryable settling verdict because the durable window could not be kept;
			// the next drain re-observes the same conflict and re-arms it. A failed CLOSE is equally
			// non-fatal — the mint already succeeded, and the next one closes the window instead.
			console.warn("[Upload] File upload settling-window checkpoint failed", { sessionId }, windowError);
		}
	});
}

export function createAdminPersistedTargetInitiator(): PersistedTargetInitiator<FileUploadSession, MultipartUploadTarget> {
	return {
		async initiatePersisted(input) {
			if (input.session.targetPending && input.session.protocolSelectionPending) await prepareAdminUploadObjectSource(input.session);
			if (input.session.targetPending && !(await beginDeferredFileUploadTarget(input.session))) {
				throw new Error(`Upload session ${input.session.id} changed before target initiation`);
			}
			// LSP-19912: only the `/uploads` family reaches `ensureUploadSession`, the admission gate
			// that now answers a `sealed`/`processing`/releasing owner with the RETRYABLE
			// `UPLOAD_OPERATION_IN_PROGRESS` instead of the previous terminal `REPLACEMENT_IN_PROGRESS`.
			// `adminChunkedUploadDriver.classifyFailure` answers that code with `retry`, which releases
			// the row to the paced drain WITHOUT rethrowing — so a permanently wedged owner would keep
			// this durable large-file/video session re-initiating forever, with no escape to a
			// user-visible needs-attention state. The shared ceiling turns it into
			// `UPLOAD_SETTLING_EXPIRED`, which that same classifier parks on. Legacy-family rows never
			// reach that gate and are left exactly as they were.
			//
			// A mint that SUCCEEDS closes the window in the same wrapper: this session re-mints its
			// target many times over its life (recover-lost, an expired presigned URL, a superseded
			// target), so leaving the first conflict's instant armed would measure a later, unrelated
			// conflict from an instant already hours past its ceiling and park it on sight.
			if (input.session.endpointFamily !== "uploads") return initiateAdminPersistedTarget(input);
			return mintPersistedTargetBoundedBySettlingCeiling(input.session, () => initiateAdminPersistedTarget(input));
		},
		// Reserve at pick: the same body the lazy mint will send, through the same shared client.
		async reservePersisted({ session, stableId, totalChunks }) {
			if (session.endpointFamily !== "uploads") return;
			await scopedAdminUploadApi(api, session).reserveUploadsUpload(persistedUploadsCreateBody(session, stableId, totalChunks).body);
		},
		// The shared driver passes { preserveRecordingSession: session.fileCategory === "video" } identically to
		// both apps' ports. Declaring the parameter is what makes it reachable — an adapter that only took
		// `target` silently dropped the flag on every call, so a video session's minted-but-unkept target aborted
		// with the server's default and soft-deleted the pending recording session the retry still needed.
		async abortPersisted(target, options) {
			if (isUploadsUploadTarget(target)) {
				await abortUploadsMultipartTarget(target);
				return;
			}
			await api.abortMultipartUpload(target.uploadId, target.s3Key, target.bucket, false, {
				preserveRecordingSession: options?.preserveRecordingSession ?? false
			});
		}
	};
}

/**
 * The `/uploads` create body for a persisted session — the ONE body both the lazy mint
 * (`initiateAdminPersistedTarget`) and reserve at pick (`reservePersisted`) send, so the identity the
 * server reserved is exactly the identity it is later asked to initiate.
 */
function persistedUploadsCreateBody(
	session: FileUploadSession,
	stableId: string,
	totalChunks: number
): { body: UploadsCreateRequest & Record<string, unknown>; rebuilt: ReturnType<typeof rebuildPersistedChunkManifest> } {
	if (session.fileCategory === "video") {
		// Same multipart lane as initiateVideo above - a re-minted persisted video is the same
		// selected FILE, so it must not fall back to the live-capture transport.
		//
		// A reloaded tab has no File to walk, so a chunked-object row rebuilds its manifest from
		// the two numbers it persisted. The rebuild is pure in (fileSize, chunkSizeBytes),
		// which is exactly why those two are the only things the row had to keep.
		const rebuilt = rebuildPersistedChunkManifest(session, totalChunks);
		return {
			rebuilt,
			body: {
				kind: "recording",
				conversationId: session.conversationId,
				sessionClientId: stableId,
				recordingClientId: stableId,
				fileType: session.fileType,
				totalChunks,
				supportedTransports: rebuilt ? ["chunked-object", "multipart"] : ["multipart"],
				...(rebuilt ? { chunkManifest: rebuilt } : {}),
				expectedBytes: session.fileSize,
				projectChecklistItemId: session.projectChecklistItemId
			}
		};
	}
	return {
		rebuilt: null,
		body: {
			kind: session.fileCategory === "image" ? "image" : "file",
			conversationId: session.conversationId,
			fileDetails: [
				{
					originalFileName: session.fileName,
					fileExtension: persistedFileExtension(session),
					fileType: session.fileType,
					idempotencyId: stableId,
					...(session.fileCategory === "image" && session.photoTime ? { createdAt: session.photoTime } : {})
				}
			],
			type: session.fileCategory === "image" ? constants.DOCUMENT_TYPE.IMAGE : constants.DOCUMENT_TYPE.DOCUMENT,
			supportedTransports: ["multipart"],
			expectedBytes: session.fileSize,
			totalChunks,
			projectChecklistItemId: session.projectChecklistItemId
		}
	};
}

async function prepareAdminUploadObjectSource(session: FileUploadSession) {
	const client = scopedAdminUploadApi(api, session)._uploadsFacade();
	const chunkSize = session.chunkSizeBytes ?? Number.NaN;
	return prepareKnownSizeUploadSource({
		telemetry: adminUploadTelemetry,
		client,
		scope: {
			kind: session.fileCategory === "video" ? "recording" : session.fileCategory === "image" ? "image" : "file",
			conversationId: session.conversationId
		},
		row: session,
		unbound: !!session.targetPending,
		byteSize: session.fileSize,
		contentType: session.fileType,
		chunkSize,
		totalParts: session.totalChunks,
		readPart: async (offset, size) => {
			const buffer = await getChunkBuffer(session.id, offset / chunkSize);
			if (!buffer || buffer.byteLength !== size) throw new Error("Retained upload part is unavailable");
			return new Blob([buffer]);
		},
		commitChoice: async (choice) => {
			const current = await commitFileUploadObjectChoice(session, choice);
			Object.assign(session, current);
			return current;
		}
	});
}

/** Cancellation replays admission to learn its fence; it must never sign or complete the source. */
export async function replayAdminPersistedObjectBinding(session: FileUploadSession, stableId: string, totalChunks: number) {
	const control = readUploadKnownSizeControl(session);
	if (!control) throw new Error("Cancellation has no saved object source");
	const { snapshot } = await bindKnownSizeUploadSource(
		scopedAdminUploadApi(api, session)._uploadsFacade(),
		persistedUploadsCreateBody(session, stableId, totalChunks).body,
		control
	);
	return {
		family: "uploads" as const,
		registryId: snapshot.id,
		attemptId: snapshot.attemptId,
		generation: snapshot.generation,
		takeGeneration: snapshot.takeGeneration
	};
}

/** The unchanged persisted-target mint, extracted so the settling ceiling can wrap it. */
export async function initiateAdminPersistedTarget({
	session,
	stableId,
	totalChunks
}: Parameters<PersistedTargetInitiator<FileUploadSession, MultipartUploadTarget>["initiatePersisted"]>[0]): Promise<
	MultipartUploadTarget | AdminVideoTarget
> {
	const requestApi = scopedAdminUploadApi(api, session);
	if (session.endpointFamily === "uploads") {
		if (session.contractVersion === 2 || session.protocolSelectionPending) {
			const control = await prepareAdminUploadObjectSource(session);
			if (control)
				return createKnownSizeUploadTarget(
					requestApi._uploadsFacade(),
					persistedUploadsCreateBody(session, stableId, totalChunks).body,
					control
				);
		}

		if (session.fileCategory === "video") {
			// Body (multipart lane, rebuilt chunked-object manifest) built by persistedUploadsCreateBody.
			const { body, rebuilt } = persistedUploadsCreateBody(session, stableId, totalChunks);
			const persistedTarget = await createAdminUploadsTarget(body, requestApi);
			// Re-attach the lane block the persisted row already owns. Without it the durable write
			// that follows this re-mint (`resetForReinitialization` → `targetPersistenceFields`) would
			// keep `transport: "chunked-object"` while blanking the chunk size and priority order, and
			// the NEXT re-mint would find no usable chunk size and silently demote to multipart.
			if (rebuilt && isUploadsUploadTarget(persistedTarget) && persistedTarget.transport === "chunked-object") {
				return {
					...persistedTarget,
					chunkedObject: {
						chunkSizeBytes: session.chunkSizeBytes as number,
						priorityChunkIndexes: parsePersistedPriorityChunkIndexes(session.priorityChunkIndexesJson, session.id)
					}
				} satisfies AdminVideoTarget;
			}
			return persistedTarget;
		}
		const response = await requestApi.createUploadsUpload(persistedUploadsCreateBody(session, stableId, totalChunks).body);
		return await uploadsTargetFromResponse(response, requestApi);
	}
	return initiateLegacyPinnedPersistedTarget(session, stableId, totalChunks, requestApi);
}
